/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
/* eslint-disable react/require-default-props, @typescript-eslint/no-unused-expressions */
import {
  FormControl, Grid, InputLabel, Tooltip, Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useDropzone, Accept, FileError } from 'react-dropzone';
import { AiOutlineEye } from 'react-icons/ai';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import { confirmWrapper } from 'src/components/Dialog';
import { CloseIcon, LinkIcon, UploadIcon } from 'src/icons';
import { twMerge } from 'tailwind-merge';
import { CommonElementProps } from '../types';
import { getContentType } from './utils';

export type FileServerValue = {
  presingnedUrl: string;
  fileName: string;
  fileKey: string;
};

export type FileValueType = File | null | FileServerValue;

export enum FileFormat {
  PDF = 'application/pdf',
  PNG = 'image/png',
  JPG = 'image/jpeg',
  DOC = 'application/msword',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  TXT = 'text/plain',
}

const fileFormatToString = (fileFormat: FileFormat): string => {
  switch (fileFormat) {
    case FileFormat.PDF:
      return 'PDF';
    case FileFormat.JPG:
      return 'JPG';
    case FileFormat.PNG:
      return 'PNG';
    case FileFormat.DOC:
      return 'DOC';
    case FileFormat.DOCX:
      return 'DOCX';
    case FileFormat.TXT:
      return 'TXT';
    default:
      return 'UNKNOWN';
  }
};

export const getFileSize = (size: number) => {
  if (!size) return '';
  if (size < 1000) {
    return `${size} bytes`;
  }
  if (size < 1000000) {
    return `${(size / 1000).toFixed(2).replace(/\.00$/, '')} KB`;
  }
  return `${(size / 1000000).toFixed(2).replace(/\.00$/, '')} MB`;
};

const truncateFileName = (fileName: string, maxLength: number): string => {
  if (fileName.length <= maxLength) {
    return fileName;
  }

  const extensionIndex = fileName.lastIndexOf('.');
  const extension = fileName.substring(extensionIndex);
  const truncatedName = fileName.substring(0, maxLength - extension.length - 4);

  return `${truncatedName}.....${extension}`;
};

export const getErrorMessage = (errors: FileError[], minSize: number, maxSize: number) => {
  for (let i = 0; i < errors.length; i += 1) {
    if (errors[i].code === 'file-invalid-type') {
      return 'Invalid file type';
    }
    if (errors[i].code === 'file-too-large') {
      return `File size too large. The file size must be less than ${getFileSize(maxSize)}`;
    }
    if (errors[i].code === 'file-too-small') {
      return `File size too small. The file size must be more than ${getFileSize(minSize)}`;
    }
  }

  return '';
};

export const getConcatinatedSupportFiles = (supportFiles: Accept) => {
  if (Object.keys(supportFiles).length > 0) {
    const concatinatedSupportFiles: string[] = [];
    Object.keys(supportFiles).forEach((supportFile: string) => {
      concatinatedSupportFiles.push(fileFormatToString(supportFile as FileFormat));
    });
    return concatinatedSupportFiles.join(', ');
  }
  return '';
};

export interface FileProps extends CommonElementProps<FileValueType | undefined> {
  minSize: number;
  maxSize: number;
  supportTypes: Accept;
  isMandatory?: boolean;
  onChange?: (file: FileValueType) => void;
  children?: React.ReactNode;
  onClose?: () => void;
}
const FileComponent = function FileComponent(props: FileProps) {
  const {
    label, maxSize = 1024 * 1024,
    minSize = 500 * 1024,
    supportTypes,
    isMandatory,
    onChange,
    error,
    value,
    disabled,
    children,
    onClose,
  } = props;
  const [files, setFiles] = useState<File[]>(value && value instanceof File ? [value as File] : []);

  React.useEffect(() => {
    if (value === undefined) {
      setFiles([]);
    }
  }, [value]);

  const {
    fileRejections,
    getRootProps,
    getInputProps,
  } = useDropzone({
    accept: supportTypes,
    multiple: false,
    minSize,
    disabled,
    maxSize,
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        if (onChange) {
          onChange(acceptedFiles[0]);
        }
        if (!children) setFiles(acceptedFiles);
      }
    },
    onFileDialogCancel: () => {
      if (onChange) {
        onChange(null);
      }
    },
    onDropRejected: () => {
      if (onChange) {
        onChange(null);
      }
    },
  });

  const cancelSelection = () => {
    setFiles([]);
    if (onChange) {
      onChange(null);
    }
    onClose && onClose();
  };

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    <div className="flex flex-col text-left" key={file.name}>
      {/* <span className="text-xs">
        Selected file size:
        {getFileSize(file.size)}
      </span> */}
      <div>
        <div className="text-[10px] text-[red]">
          {getErrorMessage(errors, minSize, maxSize)}
        </div>
      </div>
    </div>
  ));

  const previewFile = async () => {
    const file = (value as FileServerValue);
    await confirmWrapper(
      '',
      {
        variant: '',
        hidePrimaryBtn: true,
        hideSecondaryBtn: true,
        maxWidth: 'md',
        fullWidth: true,
        containerClasses: 'pt-10',
        showCloseButton: true,
        disableBackdropClickClose: true,
        disableEscapeKeyDownClose: true,
        dialogContent: (
          <div className="max-h-[80vh]">
            <DocViewer
              documents={[{
                uri: file.presingnedUrl,
                fileType: getContentType(file.fileName),
              }]}
              pluginRenderers={DocViewerRenderers}
            />
          </div>
        ),
      },
    );
  };

  if (value && (value as FileServerValue).presingnedUrl) {
    return (
      <FormControl className="w-full relative">
        <InputLabel error={!!error} required={isMandatory} className="text-[16px] font-bold leading-[23px] -ml-3 -mt-[9px] text-[#28364C]">{label}</InputLabel>
        <div className="relative mt-5">
          <div className="relative">
            <Grid container className="border-dashed border-[1px] rounded-md justify-center items-center bg-[#fff] border-[#D9D9D9] custom-shadow">
              <Grid item md={2} onClick={() => previewFile()} className="flex py-2 justify-center items-center rounded-l-md bg-[#EAEBED] cursor-pointer">
                <AiOutlineEye className="w-6 h-5" />
              </Grid>
              <Grid item md={10} className="flex items-center justify-center text-sm tex-[#28364C]">
                <Tooltip title={(value as FileServerValue).fileName}>
                  <Typography variant="caption" className="whitespace-nowrap text-ellipsis max-w-[90%]">
                    {truncateFileName((value as FileServerValue).fileName, 20)}
                  </Typography>
                </Tooltip>
              </Grid>
            </Grid>
            {!disabled && (
              <div className="absolute -right-2 -top-2" onClick={cancelSelection}>
                <CloseIcon className="cursor-pointer fill-[#fff] stroke-primary" />
              </div>
            )}
          </div>
        </div>
      </FormControl>
    );
  }

  if (value instanceof File) {
    return (
      <FormControl className="w-full relative">
        <InputLabel error={!!error} required={isMandatory} className="text-[16px] font-bold leading-[23px] -ml-3 -mt-[9px] text-[#28364C]">
          {label}
        </InputLabel>
        <div className="relative mt-[1.65rem] cursor-pointer">
          <div className="relative">
            <Grid container className="border-dashed border-[1px] rounded-md justify-center items-center bg-[#fff] border-[#D9D9D9] custom-shadow">
              <Grid item md={2} className="flex py-2 justify-center items-center rounded-l-md bg-[#EAEBED]">
                <LinkIcon className="w-6 h-5" />
              </Grid>
              <Grid item md={10} className="flex items-center justify-center text-sm tex-[#28364C]">
                <Tooltip title={(value as File).name}>
                  <Typography variant="caption" className="whitespace-nowrap text-ellipsis max-w-[90%]">
                    {truncateFileName((value as File).name, 20)}
                  </Typography>
                </Tooltip>
              </Grid>
            </Grid>
            {!disabled && (
              <div className="absolute -right-2 -top-2" onClick={cancelSelection}>
                <CloseIcon className="cursor-pointer fill-[#fff] stroke-primary" />
              </div>
            )}
          </div>
        </div>
        <div className="absolute top-[78px] -bottom-[60%]" />
      </FormControl>
    );
  }

  return (
    <FormControl className="w-full relative">
      <InputLabel error={!!error} required={isMandatory} className="text-[16px] font-bold leading-[23px] -ml-3 -mt-[9px] text-[#28364C]">{label}</InputLabel>
      <div className={twMerge('relative mt-[1.65rem] cursor-pointer', children && 'mt-0')}>
        {files?.length === 0 ? (
          <div {...getRootProps({ className: 'dropzone' })}>
            {!children && (
              <Grid
                container
                className={twMerge(
                  'border-dashed border-[1px] rounded-md justify-center items-center bg-[#fff] border-[#D9D9D9]  h-[40px] custom-shadow',
                  error && 'border-error',
                )}
              >
                <Grid item md={2} className="flex h-full justify-center items-center rounded-l-md bg-[#EAEBED]">
                  <UploadIcon className="w-6 h-5" />
                </Grid>
                <Grid item md={10} className="flex text-secondary items-center justify-center text-sm tex-[#28364C]">
                  Upload
                </Grid>
              </Grid>
            )}
            {children}
            <input {...getInputProps()} />
          </div>
        ) : (
          <div className="relative">
            <Grid container className="border-dashed border-[1px] rounded-md justify-center items-center bg-[#fff] border-[#D9D9D9] custom-shadow">
              <Grid item md={2} className="flex py-2 justify-center items-center rounded-l-md bg-[#EAEBED]">
                <LinkIcon className="w-6 h-5" />
              </Grid>
              <Grid item md={10} className="flex items-center justify-center text-sm tex-[#28364C]">
                <Tooltip title={files[0].name}>
                  <Typography variant="caption" className="whitespace-nowrap text-ellipsis max-w-[90%]">
                    {truncateFileName(files[0].name, 20)}
                  </Typography>
                </Tooltip>
              </Grid>
            </Grid>
            {!disabled && (
              <div className="absolute -right-2 -top-2" onClick={cancelSelection}>
                <CloseIcon className="cursor-pointer fill-[#fff] stroke-primary" />
              </div>
            )}
          </div>
        )}

        {
          !children && (
            <div className={twMerge('text-xs text-[#828A97] text-left absolute', children && 'text-center inset-x-0')}>
              {`Accepted files ${getConcatinatedSupportFiles(supportTypes)} < ${getFileSize(maxSize)}`}
            </div>
          )
        }
      </div>
      <div className="absolute top-[78px] -bottom-[60%]">{fileRejectionItems}</div>
    </FormControl>
  );
};

FileComponent.defaultProps = {
  isMandatory: false,
  onChange: () => { },
  children: undefined,
};

FileComponent.propTypes = {

};
export default FileComponent;
